<script setup lang="ts">
import BaseLoader from '@/components/BaseComponents/BaseLoader.vue';
import TheNavBar from '@/components/NavBar/TheNavBar.vue';
import { useDeepLink } from '@/composables/useDeepLink';
import { useOnboarding } from '@/composables/useOnboarding';
import { usePlatform } from '@/composables/usePlatform';
import { useSafeArea } from '@/composables/useSafeArea';
import { useVersionCheck } from '@/composables/useVersionCheck';
import { useUserStore } from '@/stores/useUserStore';
import { App as CapacitorApp } from '@capacitor/app';
import ConfirmPopup from 'primevue/confirmpopup';
import Toast from 'primevue/toast';
import { computed, onMounted, onUnmounted, ref, watchEffect } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import AppUpdateDialog from './components/Dialog/AppUpdateDialog.vue';
import CreditWarningDialog from './components/Dialog/CreditWarningDialog.vue';
import NoMoreCreditDialog from './components/Dialog/NoMoreCreditDialog.vue';
import OnboardingDialog from './components/Dialog/OnboardingDialog.vue';
import PWAInstall from './components/Dialog/PWAInstall.vue';
import SubscriptionChangedDialog from './components/Dialog/SubscriptionChangedDialog.vue';
import TrialEndedDialog from './components/Dialog/TrialEndedDialog.vue';
import TrialPopupDialog from './components/Dialog/TrialPopupDialog.vue';
import TheHeader from './components/Header/TheHeader.vue';
import ChapterNavigation from './components/Onboarding/ChapterNavigation.vue';
import router from './router';
import { useAppStore, type BeforeInstallPromptEvent } from './stores/appStore.js';

const isAppReady = computed(() => useAppStore().appReady);
const { platformClasses, isNative, initPlatform } = usePlatform();
const { initSafeArea, topSafeArea, bottomSafeArea, bottomSpace } = useSafeArea();
const { initVersionCheck, shouldShowUpdateDialog, dismissUpdate, needsUpdate, forceUpdate, updateMessage, storeUrl, checkVersion } =
  useVersionCheck();
const { initDeepLink, cleanup: cleanupDeepLink } = useDeepLink();

// Onboarding state
const { isChapterNavigationVisible, handleChapterNavigationClose, getCurrentChapter, getProgress, navigateToChapter } = useOnboarding();

// Flag pour éviter le conflit entre navigation et fermeture
const isNavigatingToChapter = ref(false);

// Version update dialog state
const showUpdateDialog = ref(false);

watchEffect(() => {
  // console.log(locale.value)
  // console.log(isAppReady.value)
  console.log('[App.vue] isChapterNavigationVisible:', isChapterNavigationVisible.value);
});

const route = ref(useRoute());

const toastPosition = computed(() => {
  return window.innerWidth < 768 ? 'top-right' : 'top-center';
});

const toastStyle = computed(() => ({
  top: `calc(${topSafeArea} + 1rem)`,
}));

const containerClass = computed(() => ({
  'mb-4': route.value.meta.footerConfig?.hide,
  container: route.value.meta.containerMargins,
  ...platformClasses.value,
}));

// Use inline style for dynamic padding when navbar is visible
const containerStyle = computed(() => {
  const styles: Record<string, string> = {};

  // Handle navbar visibility
  if (!route.value.meta.footerConfig?.hide) {
    // Navbar is visible - add padding for navbar + safe area
    styles.paddingBottom = bottomSpace;
  } else if (isNative.value) {
    // Navbar hidden but native - just add safe area
    styles.paddingBottom = bottomSafeArea;
  }

  // Handle header visibility for top padding
  if (route.value.meta.headerConfig?.hide) {
    // Header is hidden - add top safe area on native
    if (isNative.value) {
      styles.paddingTop = topSafeArea;
    }
  }

  return styles;
});

const userStore = useUserStore();

// Version update handlers
const handleUpdateNow = () => {
  // Open store URL but DON'T hide the dialog if it's a force update
  if (storeUrl.value) {
    window.open(storeUrl.value, '_blank');
  }

  // Only hide dialog for optional updates
  if (!forceUpdate.value && !needsUpdate.value) {
    showUpdateDialog.value = false;
  }
  // For force updates, dialog stays visible
};

const handleDismissUpdate = async () => {
  await dismissUpdate();
  showUpdateDialog.value = false;
};

// Check if update dialog should be shown
async function checkForUpdates() {
  console.log('[checkForUpdates] Checking for updates...');
  const shouldShow = await shouldShowUpdateDialog();
  if (shouldShow) {
    showUpdateDialog.value = true;
  }
}

// Handle app resume (user returns from background)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let appStateListener: any;
let focusListener: (() => void | Promise<void>) | null = null;

onMounted(async () => {
  console.log('[App.vue] App mounted - starting initialization');

  // Initialize platform features first (including status bar)
  await initPlatform();

  // Initialize safe area after platform
  await initSafeArea();

  // Initialize deep link handling for native platforms
  if (isNative.value) {
    console.log('[App.vue] Initializing deep link handling');
    await initDeepLink();
  }

  // Check for magic link redirect - IMPROVED
  const magicLinkRedirect = sessionStorage.getItem('magic_link_redirect');
  const authToken = localStorage.getItem('auth_token');

  if (magicLinkRedirect && authToken && !isNative.value) {
    console.log('[App] Magic link authentication detected, initializing user...');
    try {
      await userStore.initUser();

      // Clean up session storage BEFORE redirect
      sessionStorage.removeItem('magic_link_redirect');

      // Only redirect if we're not already at the target
      if (window.location.pathname !== magicLinkRedirect) {
        console.log('[App] Redirecting to:', magicLinkRedirect);
        router.replace(magicLinkRedirect); // Use replace instead of push
      } else {
        console.log('[App] Already at target path:', magicLinkRedirect);
      }
    } catch (error) {
      console.error('[App] Failed to initialize user from magic link:', error);
      localStorage.removeItem('auth_token');
      sessionStorage.removeItem('magic_link_redirect');
      sessionStorage.removeItem('magic_link_validated');
    }
  }

  // Initialize version checking
  console.log('[App.vue] Initializing version check...');
  await initVersionCheck();

  // Check if we should show update dialog
  console.log('[App.vue] Checking if update dialog should show...');
  await checkForUpdates();
  console.log('[App.vue] Version check complete, showUpdateDialog:', showUpdateDialog.value);

  // Listen for app resume on native platforms
  if (isNative.value) {
    appStateListener = await CapacitorApp.addListener('appStateChange', async (state) => {
      if (state.isActive) {
        // Only refresh session for authenticated routes — avoids 401-triggered redirect to login
        // when the user is on a public page (e.g. reset-password deep link)
        const currentRoute = router.currentRoute.value;
        if (currentRoute.meta.securityConfig?.requireAuthentication) {
          await userStore.ensureFreshSession();
        }
        // App came to foreground - re-check version
        console.log('App resumed, checking for updates...');
        await checkVersion(true); // Force a fresh check
        await checkForUpdates();
      }
    });
  }

  // On web, revalidate the session when the tab gains focus (only for authenticated routes)
  focusListener = async () => {
    const currentRoute = router.currentRoute.value;
    if (currentRoute.meta.securityConfig?.requireAuthentication) {
      await userStore.ensureFreshSession();
    }
  };
  window.addEventListener('focus', focusListener);

  window.addEventListener('beforeinstallprompt', (e) => {
    console.log('PWA can be installed - event captured at App level');
    e.preventDefault();
    useAppStore().deferredPrompt = e as BeforeInstallPromptEvent;
  });

  window.addEventListener('appinstalled', () => {
    console.log('PWA was installed successfully');
    useAppStore().deferredPrompt = null;
    useAppStore().isPwaInstalled = true;
  });
});

onUnmounted(() => {
  // Clean up listeners
  if (appStateListener) {
    appStateListener.remove();
  }
  if (focusListener) {
    window.removeEventListener('focus', focusListener);
  }

  // Clean up deep link listener
  if (isNative.value) {
    cleanupDeepLink();
  }
});

// ChapterNavigation handlers
function handleChapterNavigate(chapterKey: string) {
  console.log('[App.vue] Navigation vers chapitre:', chapterKey);
  // Marquer qu'on est en train de naviguer
  isNavigatingToChapter.value = true;
  // Naviguer directement vers le chapitre choisi
  navigateToChapter(chapterKey);
  // Reset du flag après un délai
  setTimeout(() => {
    isNavigatingToChapter.value = false;
  }, 1000);
}

function handleChapterNavigationCloseLocal() {
  // Si on est en train de naviguer vers un chapitre, ne pas traiter la fermeture
  if (isNavigatingToChapter.value) {
    console.log('[App.vue] Fermeture ignorée car navigation en cours');
    return;
  }
  handleChapterNavigationClose();
}
</script>

<template>
  <PWAInstall />
  <Toast :position="toastPosition" :pt="{ root: { style: toastStyle } }" />
  <ConfirmPopup />
  <div class="mx-auto max-w-[40rem]" :class="platformClasses">
    <TheHeader v-if="!route.meta.headerConfig?.hide && isAppReady" />
    <div v-if="!isAppReady" class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
      <BaseLoader />
    </div>
    <div v-else :class="containerClass" :style="containerStyle">
      <RouterView />
    </div>
    <TheNavBar v-if="!route.meta.footerConfig?.hide && isAppReady" />

    <!-- Version Update Dialog -->
    <AppUpdateDialog
      v-model:visible="showUpdateDialog"
      :force-update="forceUpdate || needsUpdate"
      :message="updateMessage"
      :store-url="storeUrl"
      @update-now="handleUpdateNow"
      @dismiss="handleDismissUpdate"
    />

    <!-- User Dialogs -->
    <CreditWarningDialog v-model:visible="userStore.showCreditWarning" :credits-left="userStore.remainingCredits" />
    <NoMoreCreditDialog v-model:visible="userStore.showNoMoreCreditDialog" />
    <SubscriptionChangedDialog v-model:visible="userStore.showSubscriptionChangedDialog" />
    <TrialPopupDialog v-model:visible="userStore.showTrialPopup" />
    <div
      v-if="
        router.currentRoute.value.name !== 'subscription-plans' &&
        router.currentRoute.value.name !== 'profile' &&
        router.currentRoute.value.name !== 'my-profile' &&
        router.currentRoute.value.name !== 'subscription-plan-confirm' &&
        router.currentRoute.value.name !== 'my-restaurant' &&
        router.currentRoute.value.name !== 'subscription-view' &&
        router.currentRoute.value.name !== 'no-restaurant'
      "
    >
      <TrialEndedDialog v-model:visible="userStore.showTrialEndedDialog" />
    </div>
    <OnboardingDialog v-if="userStore.showOnboardingDialog" />

    <!-- Chapter Navigation for Onboarding -->
    <ChapterNavigation
      v-model:visible="isChapterNavigationVisible"
      :current-chapter="getCurrentChapter() || undefined"
      :completed-chapters="getProgress()"
      @navigate="handleChapterNavigate"
      @close="handleChapterNavigationCloseLocal"
    />
  </div>
</template>

<style>
/* Platform-specific styles */

.is-offline::before {
  content: '⚠️ Offline';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #f59e0b;
  color: white;
  text-align: center;
  padding: 0.5rem;
  z-index: 9999;
}
</style>
